import { Fragment, useCallback, useEffect, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { CashEntryFormModal } from '../components/CashEntryFormModal';
import { KiemKeQuyModal } from '../components/KiemKeQuyModal';
import { useAuth } from '../context/AuthContext';
import { cashEntriesApi, type CashEntry, type CashEntryType, type CashMethod, type CashSummary } from '../lib/api/cash-entries';
import { chartOfAccountsApi, type Account } from '../lib/api/chart-of-accounts';
import { exportSheet, fmtDateISO } from '../lib/export-generic';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';

interface FormPreset { entryType: CashEntryType; method: CashMethod }

const CASH_ACCOUNT_CONFIG: Array<{ method: CashMethod; code: string; fallbackName: string }> = [
  { method: 'cash_1111', code: '1111', fallbackName: 'Tiền mặt' },
  { method: 'bank_1121', code: '1121', fallbackName: 'Tiền gửi ngân hàng' },
];

/** Weekday tiếng Việt cho day-group header. */
const WEEKDAYS = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];

/** Màu pill TK đối ứng theo prototype. */
const OFFSET_ACCOUNT_STYLE: Record<string, { bg: string; color: string }> = {
  '6421': { bg: '#eff6ff', color: '#1d4ed8' },
  '6411': { bg: '#eff6ff', color: '#1d4ed8' },
  '6417': { bg: '#eff6ff', color: '#1d4ed8' },
  '6423': { bg: '#eff6ff', color: '#1d4ed8' },
  '6427': { bg: '#eff6ff', color: '#1d4ed8' },
  '6428': { bg: '#eff6ff', color: '#1d4ed8' },
  '1411': { bg: '#fef3c7', color: '#a16207' },
  '141': { bg: '#fef3c7', color: '#a16207' },
  '5111': { bg: '#f0fdf4', color: '#15803d' },
  '5113': { bg: '#f0fdf4', color: '#15803d' },
  '511': { bg: '#f0fdf4', color: '#15803d' },
  '131': { bg: '#f0fdf4', color: '#15803d' },
  '3341': { bg: '#f5f3ff', color: '#7c3aed' },
  '334': { bg: '#f5f3ff', color: '#7c3aed' },
  '331': { bg: '#fef2f2', color: '#b91c1c' },
  '333': { bg: '#fef2f2', color: '#b91c1c' },
  '3388': { bg: '#f1f5f9', color: '#475569' },
  '1381': { bg: '#f1f5f9', color: '#475569' },
  '632': { bg: '#fef2f2', color: '#b91c1c' },
  '635': { bg: '#fef2f2', color: '#b91c1c' },
};

function offsetPillStyle(code: string): React.CSSProperties {
  const key = OFFSET_ACCOUNT_STYLE[code];
  if (key) return { background: key.bg, color: key.color };
  // fallback theo prefix
  const first = code[0];
  if (first === '6' || first === '8') return { background: '#fef2f2', color: '#b91c1c' };
  if (first === '5' || first === '7') return { background: '#f0fdf4', color: '#15803d' };
  if (first === '1' || first === '2') return { background: '#eff6ff', color: '#1d4ed8' };
  if (first === '3' || first === '4') return { background: '#f5f3ff', color: '#7c3aed' };
  return { background: '#f1f5f9', color: '#475569' };
}

function groupByDate(entries: CashEntry[]): Array<{ dateKey: string; date: Date; rows: CashEntry[] }> {
  const map = new Map<string, { date: Date; rows: CashEntry[] }>();
  for (const e of entries) {
    const d = new Date(e.entryDate);
    const key = d.toISOString().slice(0, 10);
    const bucket = map.get(key) ?? { date: d, rows: [] };
    bucket.rows.push(e);
    map.set(key, bucket);
  }
  return Array.from(map.entries())
    .map(([dateKey, v]) => ({ dateKey, date: v.date, rows: v.rows }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

function shortDate(iso: string | Date) {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function longDate(d: Date) {
  return `${WEEKDAYS[d.getDay()]}, ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthRange(value: string) {
  const [year, month] = value.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return {
    from: `${value}-01`,
    to: `${value}-${String(lastDay).padStart(2, '0')}`,
    label: `Tháng ${String(month).padStart(2, '0')}/${year}`,
    startLabel: `01/${String(month).padStart(2, '0')}/${year}`,
    endLabel: `${String(lastDay).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`,
  };
}

export function CashLedgerPage() {
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const [tab, setTab] = useState<CashMethod>('cash_1111');
  const [entries, setEntries] = useState<CashEntry[]>([]);
  const [summary, setSummary] = useState<CashSummary | null>(null);
  const [cashAccounts, setCashAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState(currentMonthValue);
  const [preset, setPreset] = useState<FormPreset | null>(null);
  const [kkqOpen, setKkqOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const isKtt = user?.role === 'KTT';
  const isNvKt = user?.role === 'NV_KT';
  const canCreate = isKtt || isNvKt;
  const range = monthRange(month);

  useEffect(() => {
    chartOfAccountsApi.listFlat({ activeOnly: 'true' })
      .then((response) => setCashAccounts(response.items))
      .catch(() => setCashAccounts([]));
  }, []);

  const tabs = CASH_ACCOUNT_CONFIG.map((config) => {
    const account = cashAccounts.find((item) => item.code === config.code);
    return { ...config, name: account?.name ?? config.fallbackName, available: cashAccounts.length === 0 || Boolean(account) };
  }).filter((item) => item.available);

  useEffect(() => {
    if (tabs.length && !tabs.some((item) => item.method === tab)) setTab(tabs[0].method);
  }, [tab, tabs]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await cashEntriesApi.list({ method: tab, from: range.from, to: range.to });
      setEntries(res.items); setSummary(res.summary);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [tab, range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  const filtered = search.trim()
    ? entries.filter((e) => {
        const q = search.trim().toLowerCase();
        return e.code.toLowerCase().includes(q)
          || e.description.toLowerCase().includes(q)
          || e.actor.toLowerCase().includes(q);
      })
    : entries;

  const groups = groupByDate(filtered);
  let running = summary?.openingBalance ?? 0;
  const filteredTotalIn = filtered.reduce((total, entry) => total + (entry.entryType === 'receipt' ? entry.amount : 0), 0);
  const filteredTotalOut = filtered.reduce((total, entry) => total + (entry.entryType === 'payment' ? entry.amount : 0), 0);

  const filterActive = search.trim().length > 0;

  return (
    <AppShell title="Sổ quỹ" subtitle="Sổ theo dõi thu-chi tiền mặt + tiền gửi ngân hàng">
      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      {/* KTT hero banner */}
      {isKtt && (
        <div className="clg-hero">
          <div className="clg-hero-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
          </div>
          <div className="clg-hero-meta">
            <b>Chế độ KTT · Xem &amp; đối chiếu</b>
            <p>NV Kế toán lập phiếu thu-chi. KTT review, đối chiếu sao kê NH và duyệt/khoá kỳ.</p>
          </div>
        </div>
      )}

      {/* KPI cards — 4 cards với stripe + icon chip + eyebrow */}
      <div className="clg-kpi-grid">
        <div className="clg-kpi clg-kpi-gray">
          <div className="clg-kpi-stripe" />
          <div className="clg-kpi-body">
            <div className="clg-kpi-head">
              <div className="clg-kpi-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
              </div>
              <div className="clg-kpi-label">Số dư đầu tháng</div>
            </div>
            <div className="clg-kpi-value">{summary ? formatVND(summary.openingBalance) : '—'}</div>
            <div className="clg-kpi-sub">Đầu kỳ {range.startLabel}</div>
          </div>
        </div>

        <div className="clg-kpi clg-kpi-green">
          <div className="clg-kpi-stripe" />
          <div className="clg-kpi-body">
            <div className="clg-kpi-head">
              <div className="clg-kpi-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17V5M12 22V2M17 17V9" /></svg>
              </div>
              <div className="clg-kpi-label">Tổng thu</div>
            </div>
            <div className="clg-kpi-value">{summary ? formatVND(summary.totalIn) : '—'}</div>
            <div className="clg-kpi-sub">{summary?.countIn ?? 0} phiếu thu</div>
          </div>
        </div>

        <div className="clg-kpi clg-kpi-red">
          <div className="clg-kpi-stripe" />
          <div className="clg-kpi-body">
            <div className="clg-kpi-head">
              <div className="clg-kpi-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 7v12M12 2v22M17 7v12" /></svg>
              </div>
              <div className="clg-kpi-label">Tổng chi</div>
            </div>
            <div className="clg-kpi-value">{summary ? formatVND(summary.totalOut) : '—'}</div>
            <div className="clg-kpi-sub">{summary?.countOut ?? 0} phiếu chi</div>
          </div>
        </div>

        <div className="clg-kpi clg-kpi-blue">
          <div className="clg-kpi-stripe" />
          <div className="clg-kpi-body">
            <div className="clg-kpi-head">
              <div className="clg-kpi-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
              </div>
              <div className="clg-kpi-label">Số dư cuối tháng</div>
            </div>
            <div className="clg-kpi-value">{summary ? formatVND(summary.closingBalance) : '—'}</div>
            <div className="clg-kpi-sub">Đến {range.endLabel}</div>
          </div>
        </div>
      </div>

      {/* Main card */}
      <div className="pay-page-card">
        {/* Sub-account tabs — pill group inside card */}
        <div className="clg-subtabs">
          {tabs.map((item) => (
            <button key={item.method} type="button" className={`clg-subtab ${tab === item.method ? 'active' : ''}`} onClick={() => setTab(item.method)}>
              {item.method === 'cash_1111'
                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1 0-4h12v4M4 6v12a2 2 0 0 0 2 2h14v-4M18 12h.01" /></svg>
                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></svg>}
              {item.name} ({item.code})
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="pay-toolbar">
          <div className="pay-toolbar-left">
            <label className="clg-month-picker" title="Chọn kỳ sổ quỹ">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
              <input aria-label="Kỳ sổ quỹ" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </label>
            <div className="pay-search" style={{ minWidth: 260 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                placeholder="Tìm mã CT, người nộp/nhận, diễn giải…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button className="pay-btn-outline" type="button" onClick={() => { setSearch(''); setMonth(currentMonthValue()); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46" /></svg>
              Xoá bộ lọc
            </button>
          </div>
          <div className="pay-toolbar-right">
            {isNvKt && (
              <button
                type="button"
                className="clg-btn-green"
                onClick={() => setPreset({ entryType: 'receipt', method: tab })}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                Phiếu thu khác
              </button>
            )}
            {canCreate && (
              <button
                type="button"
                className="pay-btn-outline"
                onClick={() => setKkqOpen(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4M12 3l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V7l8-4z" /></svg>
                Kiểm kê quỹ
              </button>
            )}
            {isKtt && (
              <>
                <button className="pay-btn-primary" type="button" onClick={() => showToast('Modal đối chiếu sao kê NH — đang phát triển (F6, KTT)')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 3v5h-5M8 21H3v-5M21 8A9 9 0 0 0 6.7 5.7L3 9M3 16a9 9 0 0 0 14.3 2.3L21 15" /></svg>
                  Đối chiếu sao kê NH
                </button>
                <button className="pay-btn-outline" type="button" onClick={() => { if (confirm(`Khoá kỳ ${range.label}?`)) showToast(`Đã yêu cầu khoá ${range.label} (mock)`); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                  Duyệt &amp; khoá kỳ
                </button>
              </>
            )}
            <div className="clg-import-wrap">
              <button className="pay-btn-outline" type="button" onClick={() => setImportOpen(!importOpen)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                Import Excel ▾
              </button>
              {importOpen && (
                <div className="clg-import-menu">
                  <div className="clg-import-head">Tiền mặt (1111)</div>
                  <button type="button" className="clg-import-item" onClick={() => { showToast('Import Phiếu chi tiền mặt Excel — đang phát triển'); setImportOpen(false); }}>
                    <span className="clg-import-badge red">PC</span> Phiếu chi tiền mặt
                  </button>
                  <button type="button" className="clg-import-item" onClick={() => { showToast('Import Phiếu thu tiền mặt Excel — đang phát triển'); setImportOpen(false); }}>
                    <span className="clg-import-badge green">PT</span> Phiếu thu tiền mặt
                  </button>
                  <div className="clg-import-head">Tiền gửi NH (1121)</div>
                  <button type="button" className="clg-import-item" onClick={() => { showToast('Import UNC chi — đang phát triển'); setImportOpen(false); }}>
                    <span className="clg-import-badge red">UNC</span> Phiếu chi qua UNC
                  </button>
                  <button type="button" className="clg-import-item" onClick={() => { showToast('Import UNC thu — đang phát triển'); setImportOpen(false); }}>
                    <span className="clg-import-badge green">UNC</span> Phiếu thu qua UNC
                  </button>
                </div>
              )}
            </div>
            <button className="pay-btn-outline" type="button" onClick={() => window.print()}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
              In sổ
            </button>
            <button
              className="pay-btn-outline"
              type="button"
              onClick={() => {
                exportSheet(`cashledger-${tab}`, filtered, [
                  { header: 'Ngày CT', key: 'entryDate', width: 12, format: (v) => fmtDateISO(v) },
                  { header: 'Số CT', key: 'code', width: 20 },
                  { header: 'Loại', key: 'entryType', width: 10, format: (v) => v === 'receipt' ? 'Thu' : 'Chi' },
                  { header: 'Diễn giải', key: 'description', width: 40 },
                  { header: 'TK ĐƯ', key: 'offsetAccount', width: 10 },
                  { header: 'Người nộp/nhận', key: 'actor', width: 25 },
                  { header: 'Số tiền', key: 'amount', width: 18, format: (v) => Number(v) },
                ], `SoQuy-${tab}`);
                showToast(`Đã xuất ${filtered.length} phiếu`);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
              Excel
            </button>
          </div>
        </div>

        {/* Active filter chip */}
        {filterActive && (
          <div className="clg-filter-chips">
            <span className="clg-chip">
              Tìm: <b>{search}</b>
              <button type="button" onClick={() => setSearch('')}>×</button>
            </span>
            <button type="button" className="clg-chip-clear" onClick={() => setSearch('')}>Xoá tất cả</button>
          </div>
        )}

        {/* Table */}
        <div className="pay-table-wrap">
          {loading ? <div className="pay-empty">Đang tải…</div> : (
            <table className="pay-tbl clg-tbl">
              <thead>
                <tr>
                  <th>Ngày CT</th>
                  <th>Số CT</th>
                  <th>Diễn giải</th>
                  <th>TK ĐƯ</th>
                  <th>Người nộp / nhận</th>
                  <th style={{ textAlign: 'right' }}>Thu</th>
                  <th style={{ textAlign: 'right' }}>Chi</th>
                  <th style={{ textAlign: 'right' }}>Số dư</th>
                </tr>
              </thead>
              <tbody>
                {/* Opening balance row */}
                <tr className="clg-open-row">
                  <td colSpan={7}>SỐ DƯ ĐẦU KỲ · {range.startLabel}</td>
                  <td style={{ textAlign: 'right' }}>{summary ? formatVND(summary.openingBalance) : '—'}</td>
                </tr>

                {groups.length === 0 && (
                  <tr>
                    <td colSpan={8} className="pay-empty">Không có phiếu nào trong kỳ.</td>
                  </tr>
                )}

                {groups.map((g) => {
                  const dayIn = g.rows.reduce((s, r) => s + (r.entryType === 'receipt' ? r.amount : 0), 0);
                  const dayOut = g.rows.reduce((s, r) => s + (r.entryType === 'payment' ? r.amount : 0), 0);
                  return (
                    <Fragment key={g.dateKey}>
                      {/* Day header — weekday + full date + count */}
                      <tr className="clg-day-row">
                        <td colSpan={8}>{longDate(g.date)} <span className="clg-day-meta">· {g.rows.length} giao dịch</span></td>
                      </tr>
                      {/* Rows */}
                      {g.rows.map((r) => {
                        const income = r.entryType === 'receipt' ? r.amount : 0;
                        const expense = r.entryType === 'payment' ? r.amount : 0;
                        running = running + income - expense;
                        return (
                          <tr key={r.id} className="clg-entry-row">
                            <td>{shortDate(r.entryDate)}</td>
                            <td className="clg-code-cell">{r.code}</td>
                            <td>{r.description}</td>
                            <td><span className="clg-offset-pill" style={offsetPillStyle(r.offsetAccount)}>{r.offsetAccount}</span></td>
                            <td>{r.actor}</td>
                            <td style={{ textAlign: 'right', color: income ? '#15803d' : '#cbd5e1' }}>{income ? formatVND(income) : '—'}</td>
                            <td style={{ textAlign: 'right', color: expense ? '#b91c1c' : '#cbd5e1' }}>{expense ? formatVND(expense) : '—'}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{formatVND(running)}</td>
                          </tr>
                        );
                      })}
                      {/* Day subtotal AFTER day rows */}
                      <tr className="clg-day-subtotal">
                        <td colSpan={5}>Cộng ngày {shortDate(g.date)}</td>
                        <td style={{ textAlign: 'right' }}>{dayIn ? formatVND(dayIn) : '—'}</td>
                        <td style={{ textAlign: 'right' }}>{dayOut ? formatVND(dayOut) : '—'}</td>
                        <td />
                      </tr>
                    </Fragment>
                  );
                })}

                {/* Grand total row — blue gradient */}
                {summary && groups.length > 0 && (
                  <tr className="clg-grand-total">
                    <td colSpan={5}>TỔNG CỘNG {range.label} ({filtered.length} giao dịch)</td>
                    <td style={{ textAlign: 'right', color: '#15803d' }}>{formatVND(filteredTotalIn)}</td>
                    <td style={{ textAlign: 'right', color: '#b91c1c' }}>{formatVND(filteredTotalOut)}</td>
                    <td style={{ textAlign: 'right' }}>{formatVND(summary.openingBalance + filteredTotalIn - filteredTotalOut)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <CashEntryFormModal
        open={preset !== null}
        preset={preset}
        onClose={() => setPreset(null)}
        onSaved={() => { setPreset(null); showToast('Đã lập phiếu + bút toán'); load(); }}
      />
      <KiemKeQuyModal
        open={kkqOpen}
        currentBalance={summary?.closingBalance ?? 0}
        method={tab}
        onClose={() => setKkqOpen(false)}
        onSaved={() => { setKkqOpen(false); showToast('Đã hoàn tất kiểm kê quỹ'); load(); }}
      />

      {toast && <div className="coa-toast">{toast}</div>}
    </AppShell>
  );
}
